import { ReactNode, useState } from "react";
import {
  IconFolder,
  IconFolderOpen,
  IconChevronRight,
  IconChevronDown,
} from "@tabler/icons-react";
import { ItemNoun, MonthFolder } from "./MonthFolder";
import { MONTH_NAMES } from "./groupByYearAndMonth";

interface YearFolderProps<T> {
  year: number;
  monthData: Record<number, T[]>;
  itemCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  getDate: (item: T) => string | Date;
  getItemKey: (item: T) => string | number;
  renderItem: (item: T) => ReactNode;
  itemNoun: ItemNoun;
  monthNames?: string[];
}

export function YearFolder<T>({
  year,
  monthData,
  itemCount,
  isExpanded,
  onToggle,
  getDate,
  getItemKey,
  renderItem,
  itemNoun,
  monthNames = MONTH_NAMES,
}: YearFolderProps<T>) {
  const [expandedMonths, setExpandedMonths] = useState<Set<number>>(new Set());

  const sortedMonths = Object.keys(monthData)
    .map(Number)
    .sort((a, b) => b - a);

  const toggleMonth = (month: number) => {
    setExpandedMonths((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(month)) {
        newSet.delete(month);
      } else {
        newSet.add(month);
      }
      return newSet;
    });
  };

  const noun = itemCount === 1 ? itemNoun.singular : itemNoun.plural;

  return (
    <div className="select-none">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 w-full p-3 rounded-lg hover:bg-slate-50 transition-colors text-left group"
      >
        <span className="text-slate-400 transition-transform duration-200">
          {isExpanded ? (
            <IconChevronDown size={18} />
          ) : (
            <IconChevronRight size={18} />
          )}
        </span>
        <span className="text-amber-500">
          {isExpanded ? (
            <IconFolderOpen size={22} />
          ) : (
            <IconFolder size={22} />
          )}
        </span>
        <span className="font-semibold text-gray-800 text-lg">{year}</span>
        <span className="text-gray-400 text-sm ml-auto">
          {itemCount} {noun}
        </span>
      </button>

      {isExpanded && (
        <div className="ml-6 border-l-2 border-slate-200 pl-2 mt-1">
          {sortedMonths.map((month) => (
            <MonthFolder
              key={month}
              monthName={monthNames[month]}
              items={monthData[month]}
              isExpanded={expandedMonths.has(month)}
              onToggle={() => toggleMonth(month)}
              getDate={getDate}
              getItemKey={getItemKey}
              renderItem={renderItem}
              itemNoun={itemNoun}
            />
          ))}
        </div>
      )}
    </div>
  );
}
